import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import FilterIcon from '../../../static/icons/filter-icon.svg';
import RedCrossIcon from '../../../static/icons/red-cross.svg';
import { formatYearDisplay } from '../../../utils/strings';

const UNASSIGNED = 'Unassigned';

const yearToNum = (y) => {
  if (y == null || String(y).toLowerCase() === UNASSIGNED.toLowerCase()) return null;
  const n = parseInt(String(y).replace(/\D/g, ''), 10);
  return Number.isNaN(n) ? null : n;
};
const yearToDisplay = (y) => (y == null || String(y).toLowerCase() === UNASSIGNED.toLowerCase() ? UNASSIGNED : formatYearDisplay(y));

const normalizeSubject = (s) => {
  if (typeof s === 'string') return { code: s, title: s };
  const code = s?.subjectCode || s?.code || '';
  const title = s?.subjectName || s?.title || s?.name || code || '';
  return { code: code || title, title };
};

const isUnassigned = (v) => v == null || String(v).toLowerCase() === 'unassigned';

const getItemYears = (item) => {
  const meta = item.metadata?.years?.length ? item.metadata.years : [];
  const direct = item.years?.length ? item.years : [];
  const single = item.year ? [item.year] : [];
  return [...new Set([...meta, ...direct, ...single])].filter(y => !isUnassigned(y));
};
const getItemSubjects = (item) => {
  const all = item.metadata?.subjects?.length ? item.metadata.subjects : (item.subjects || []);
  return all.filter((s) => {
    const code = typeof s === 'string' ? s : (s?.subjectCode || s?.code);
    return code != null && code !== '' && !isUnassigned(code);
  });
};
const getItemTaskTypes = (item) => (item.metadata?.taskTypes?.length ? item.metadata.taskTypes : null) || (item.taskTypes?.length ? item.taskTypes : null) || (item.taskType ? [item.taskType] : []);
const getItemResponseTypes = (item) => (item.metadata?.responseTypes?.length ? item.metadata.responseTypes : null) || (item.responseTypes?.length ? item.responseTypes : null) || (item.responseType ? [item.responseType] : []);

const SubjectFilter = ({
  selectedYears = [],
  setSelectedYears,
  selectedSubjects = [],
  setSelectedSubjects,
  selectedTaskTypes = [],
  setSelectedTaskTypes,
  selectedResponseTypes = [],
  setSelectedResponseTypes,
  onClearAll,
  items = [],
  yearsFromApi = null,
  itemsForFilterOptions = null,
  hiddenSubjectCodes = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const sourceItems = Array.isArray(itemsForFilterOptions) ? itemsForFilterOptions : items;

  // Collect all available options from all items — no cascading
  const { availableYears, availableSubjectsWithCode, availableTaskTypes, availableResponseTypes } = useMemo(() => {
    const allItems = Array.isArray(sourceItems) ? sourceItems : [];

    // Years
    let years;
    if (Array.isArray(yearsFromApi) && yearsFromApi.length > 0) {
      const fromApi = [...new Set(yearsFromApi.map((y) => yearToDisplay(y)).filter(Boolean))];
      years = [UNASSIGNED, ...fromApi.filter((y) => y !== UNASSIGNED)].sort((a, b) => {
        if (a === UNASSIGNED) return -1;
        if (b === UNASSIGNED) return 1;
        return (yearToNum(a) || 0) - (yearToNum(b) || 0);
      });
    } else {
      const yearSet = new Set();
      allItems.forEach((item) => {
        const itemYears = getItemYears(item);
        if (!itemYears.length) {
          yearSet.add(UNASSIGNED);
        } else {
          itemYears.forEach((y) => { if (y != null) yearSet.add(yearToDisplay(y)); });
        }
      });
      years = Array.from(yearSet).sort((a, b) => {
        if (a === UNASSIGNED) return -1;
        if (b === UNASSIGNED) return 1;
        return (yearToNum(a) || 0) - (yearToNum(b) || 0);
      });
    }

    // Subjects — from all items
    const subjectMap = new Map();
    allItems.forEach((item) => {
      const subjects = getItemSubjects(item);
      if (!subjects.length) {
        if (!subjectMap.has(UNASSIGNED.toLowerCase())) subjectMap.set(UNASSIGNED.toLowerCase(), { code: UNASSIGNED, title: UNASSIGNED });
      } else {
        subjects.forEach((s) => {
          if (s == null) return;
          const { code, title } = normalizeSubject(s);
          const key = code || title;
          // Hide subjects that don't belong to the user's selected state(s).
          // hiddenSubjectCodes is null for consumers that don't state-scope.
          if (hiddenSubjectCodes && key && hiddenSubjectCodes.has(String(key).toUpperCase())) return;
          const lowerKey = key?.toLowerCase();
          if (lowerKey && !subjectMap.has(lowerKey)) {
            subjectMap.set(lowerKey, { code: key, title: title || key });
          }
        });
      }
    });
    const subjectsList = Array.from(subjectMap.values()).sort((a, b) => a.title.localeCompare(b.title));
    // Count titles to detect duplicates — only show code when titles collide
    const titleCounts = new Map();
    subjectsList.forEach(({ title }) => titleCounts.set(title, (titleCounts.get(title) || 0) + 1));
    const subjects = subjectsList.map((s) => ({
      ...s,
      displayLabel: titleCounts.get(s.title) > 1 && s.code !== s.title ? `${s.title} (${s.code})` : s.title,
    }));

    // Task types — from all items
    const taskTypeSet = new Set();
    allItems.forEach((item) => {
      getItemTaskTypes(item).forEach((t) => { if (t) taskTypeSet.add(t); });
    });
    const taskTypes = Array.from(taskTypeSet).sort();

    // Response types — from all items
    const responseTypeSet = new Set();
    allItems.forEach((item) => {
      getItemResponseTypes(item).forEach((r) => { if (r) responseTypeSet.add(r); });
    });
    const responseTypes = Array.from(responseTypeSet).sort();

    return {
      availableYears: years,
      availableSubjectsWithCode: subjects,
      availableTaskTypes: taskTypes,
      availableResponseTypes: responseTypes,
    };
  }, [sourceItems, yearsFromApi, hiddenSubjectCodes]);

  const hasTaskTypeProps = typeof setSelectedTaskTypes === 'function';
  const hasResponseTypeProps = typeof setSelectedResponseTypes === 'function';

  const totalFilters = selectedYears.length + selectedSubjects.length
    + (hasTaskTypeProps ? selectedTaskTypes.length : 0)
    + (hasResponseTypeProps ? selectedResponseTypes.length : 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleYearToggle = (year) => {
    if (selectedYears.includes(year)) {
      setSelectedYears(selectedYears.filter(y => y !== year));
    } else {
      setSelectedYears([...selectedYears, year]);
    }
  };

  const handleSubjectToggle = (code) => {
    if (selectedSubjects.includes(code)) {
      setSelectedSubjects(selectedSubjects.filter((s) => s !== code));
    } else {
      setSelectedSubjects([...selectedSubjects, code]);
    }
  };

  const handleTaskTypeToggle = (type) => {
    if (!hasTaskTypeProps) return;
    if (selectedTaskTypes.includes(type)) {
      setSelectedTaskTypes(selectedTaskTypes.filter(t => t !== type));
    } else {
      setSelectedTaskTypes([...selectedTaskTypes, type]);
    }
  };

  const handleResponseTypeToggle = (type) => {
    if (!hasResponseTypeProps) return;
    if (selectedResponseTypes.includes(type)) {
      setSelectedResponseTypes(selectedResponseTypes.filter(r => r !== type));
    } else {
      setSelectedResponseTypes([...selectedResponseTypes, type]);
    }
  };

  const handleClearAll = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setSelectedYears([]);
    setSelectedSubjects([]);
    if (hasTaskTypeProps) setSelectedTaskTypes([]);
    if (hasResponseTypeProps) setSelectedResponseTypes([]);
    if (onClearAll) onClearAll();
  };

  const handleFilterButtonClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setIsOpen(!isOpen);
  };

  const handleDropdownClick = (e) => {
    e?.stopPropagation?.();
  };

  return (
    <FilterContainer ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
      <FilterButton
        type="button"
        onClick={handleFilterButtonClick}
        isActive={totalFilters > 0}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Filters${totalFilters > 0 ? `, ${totalFilters} active` : ''}`}
      >
        <FilterIconImg src={FilterIcon} alt="Filter" />
        <span>Filters</span>
        {totalFilters > 0 && (
          <FilterCount>{totalFilters}</FilterCount>
        )}
        <ChevronIcon isOpen={isOpen}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </ChevronIcon>
      </FilterButton>

      {isOpen && (
        <DropdownMenu onClick={handleDropdownClick}>
          <DropdownHeader>
            <DropdownTitle>
              <FilterIconImg src={FilterIcon} alt="Filters" />
              Filters
            </DropdownTitle>
            {totalFilters > 0 && (
              <ClearAllButton type="button" onClick={handleClearAll}>
                <ClearIcon>
                  <img src={RedCrossIcon} alt="" />
                </ClearIcon>
                <ClearAllText>Clear all filters</ClearAllText>
              </ClearAllButton>
            )}
          </DropdownHeader>

          {availableYears.length >= 1 && (
          <FilterSection>
            <SectionTitle>Years</SectionTitle>
            <CheckboxRowScroll>
              {availableYears.map((year) => (
                <CheckboxItem key={year}>
                  <CheckboxInput
                    type="checkbox"
                    id={`year-${year}`}
                    checked={selectedYears.includes(year)}
                    onChange={() => handleYearToggle(year)}
                  />
                  <CheckboxLabel htmlFor={`year-${year}`}>{year}</CheckboxLabel>
                </CheckboxItem>
              ))}
            </CheckboxRowScroll>
          </FilterSection>
          )}

          {availableSubjectsWithCode.length >= 1 && (
          <FilterSection>
            <SectionTitle>Subjects</SectionTitle>
            <CheckboxRowScroll>
              {availableSubjectsWithCode.map(({ code, displayLabel }) => (
                <CheckboxItem key={code}>
                  <CheckboxInput
                    type="checkbox"
                    id={`subject-${code}`}
                    checked={selectedSubjects.includes(code)}
                    onChange={() => handleSubjectToggle(code)}
                  />
                  <CheckboxLabel htmlFor={`subject-${code}`}>{displayLabel}</CheckboxLabel>
                </CheckboxItem>
              ))}
            </CheckboxRowScroll>
          </FilterSection>
          )}

          {hasTaskTypeProps && availableTaskTypes.length >= 1 && (
            <FilterSection>
              <SectionTitle>Task Type</SectionTitle>
              <CheckboxRowScroll>
                {availableTaskTypes.map((type) => (
                  <CheckboxItem key={type}>
                    <CheckboxInput
                      type="checkbox"
                      id={`tasktype-${type}`}
                      checked={selectedTaskTypes.includes(type)}
                      onChange={() => handleTaskTypeToggle(type)}
                    />
                    <CheckboxLabel htmlFor={`tasktype-${type}`}>
                      {type}
                    </CheckboxLabel>
                  </CheckboxItem>
                ))}
              </CheckboxRowScroll>
            </FilterSection>
          )}

          {hasResponseTypeProps && availableResponseTypes.length >= 1 && (
            <FilterSection>
              <SectionTitle>Submission Type</SectionTitle>
              <CheckboxRowScroll>
                {availableResponseTypes.map((type) => (
                  <CheckboxItem key={type}>
                    <CheckboxInput
                      type="checkbox"
                      id={`responsetype-${type}`}
                      checked={selectedResponseTypes.includes(type)}
                      onChange={() => handleResponseTypeToggle(type)}
                    />
                    <CheckboxLabel htmlFor={`responsetype-${type}`}>
                      {type}
                    </CheckboxLabel>
                  </CheckboxItem>
                ))}
              </CheckboxRowScroll>
            </FilterSection>
          )}
        </DropdownMenu>
      )}
    </FilterContainer>
  );
};

const FilterContainer = styled.div`
  position: relative;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => props.isActive ? '#F3E5F5' : '#ffffff'};
  border: 1px solid ${(props) => props.isActive ? '#7B1FA2' : '#c9c6cc'};
  color: ${(props) => props.isActive ? '#7B1FA2' : '#4b464f'};

  &:hover {
    border-color: #7B1FA2;
    background: #FCE4EC;
  }
`;

const FilterIconImg = styled.img`
  width: 16px;
  height: 16px;
`;

const FilterCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #7B1FA2;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9px;
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${(props) => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  display: flex;
  flex-direction: column;
  min-width: 400px;
  max-width: 440px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--color-neutral-80, #F2F1F3);
  border-radius: 12px;
  box-shadow: 0px 4px 9.8px 0px #73737340;
  z-index: 1000;
  padding: 12px 0;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
`;

const DropdownTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ClearAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #F3F4F6;
  }
`;

const ClearIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid var(--color-danger-red-300, #E2483D);

  img {
    width: 12px;
    height: 12px;
  }
`;

const ClearAllText = styled.span`
  color: var(--color-danger-red-300, #E2483D);
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 12px 16px;
  margin: 0 12px 8px;
  background: #FFFFFF;
  border-radius: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckboxRowScroll = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  max-width: 100%;
`;

const SectionTitle = styled.h4`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 10px 0;
`;

const CheckboxItem = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #7200E0;
  border-radius: 4px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #7200E0;
  }
`;

export default SubjectFilter;
